import React, {createRef, useState, useContext} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import MaterialTable from "@material-table/core";
import {TablePagination, Checkbox, Select, MenuItem, useTheme} from "@material-ui/core";

import SystemContext from "../../../SystemContext";
import { useTripMessaging, useAddTripMessaging, useUpdateTripMessaging, useDeleteTripMessaging } from "../../../services/trips";
import TripMessagingValidator from "../validators/TripMessagingValidator";
import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import { 
  MaskedText,
} from "../../../controls";
import ClientCompanyUuidLookup from "../../Companies/components/CompanyUuidLookup";
import AirportLookup from "../../Airports/components/AirportsLookup";
import MessageTypeLookup from "../../Settings/components/MessageTypeLookup";
import {formatEmailInput} from "../../../functions/EmailValidation";
import { defaultComponents } from '../../../controls/Material-Table-Components';


const formatValue = (type, value) => {
  if (type === "Email" && value)
    return(formatEmailInput(value));
}

const TripMessagingList = (_props) => {
  const {isAuthenticated} = useAuth0();
  const SystemData = useContext(SystemContext);
  const theme = useTheme();

  const props = {
    ..._props
  }
  const [state, setState] = useState({
    canEdit: props.canEdit,
    activeRow: null
  });

  const listVariant = props.listVariant;
  const tripUuid = props.tripUuid;
  const tripLegUuid = props.tripLegUuid;
  const route = props.route;

  const tableRef = createRef({state: {lastEditingRow: false}});

  const handleAddTripMessaging = useAddTripMessaging();
  const handleUpdateTripMessaging = useUpdateTripMessaging();
  const handleDeleteTripMessaging = useDeleteTripMessaging();

  const queryClient = useQueryClient();
  const extraKeys = listVariant === "TRIP" ? [tripUuid] : [tripUuid, tripLegUuid];
  const tripMessaging = useTripMessaging({tableRef, queryClient, extraKeys, listVariant });

  const ediMethods = SystemData.ediMethods;

  const getColumns = (props) => { 
    const baseColumn = {
      cellStyle: { padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
    };
    const formatChars={ 
      "U": "[A-Z]", 
      "A": "[A-Z0-9]", 
      "a": "[A-Za-z0-9]",
      "i": "[A-Za-z0-9\\-]",
      "9": "[0-9]",
      "D": "[0-9.]",
      "-": "-",
      "x": "."
    }
    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    const additionalValues = {route: route,  listVariant};
    const validatorColumn = ValidatorColumn(TripMessagingValidator, true, additionalValues);

    return [
      {...validatorColumn},
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */
      {
        ...baseColumn,
        ...ColumnWidth(140, null, null),
        title: "At Airport",
        field: "airportCode",
        editable: state.canEdit ? "onAdd" : "never",
        editComponent: (props) => (
          <AirportLookup
            value={props.value}
            onChange={props.onChange}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(110),
        title: "Message",
        field: "messageCode",
        initialEditValue: "",
        editComponent: (props) => (
          <MessageTypeLookup
            value={props.value}
            onChange={props.onChange}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(110),
        title: "Variant",
        field: "messageSubCode",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(10)}
            maskChar={""}
            forceUpperCase={true}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      // {
      //   ...baseColumn,
      //   title: "Recipient",
      //   field: "companyName",
      //   initialEditValue: "",
      //   editComponent: (props) => (
      //     <ClientCompanyUuidLookup
      //       value={props.value}
      //       returnType={"object"}
      //       onChange={(returnObject) => {
      //         if (returnObject.found) {
      //           const customer = returnObject.data
      //           const data = {
      //             ...props.rowData,
      //             companyName: customer.companyName,
      //             companyMessagingUuid: customer.companyUuid,
      //             companyCode: customer.companyCode,
      //           }
      //           props.onRowDataChange(data);
      //         } else {
      //           props.onChange(returnObject.value);
      //         }
      //       }}
      //     />
      //   ),
      // },
      {
        ...baseColumn,
        cellStyle: { paddingTop: 4 },
        headerStyle: { paddingTop: 4 },
        title: "Delivery Method",
        field: "deliveryMethod",
        initialEditValue: "",
        editComponent: (props) => (
          <Select
            labelId="Delivery Method"
            id="Delivery Method"
            value={props.value}
            onChange={e => {
              props.onChange(e.target.value);
            }}
          >
            {ediMethods.map((item) => {
              return (<MenuItem key={item} value={item}>{item}</MenuItem>);
            })
            }
          </Select>
        ),
        render: (rowData) => (<div>{rowData.deliveryMethod}</div>),
      },
      {
        ...baseColumn,
        title: "Delivery Address",
        field: "deliveryAddress",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(100)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={(value) => {
              props.onChange(formatValue(props.rowData.deliveryMethod, value));
            }}
            //onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
    ];
  };

  const border = listVariant === "TRIP" ? "2px solid #aaaaaa" : "2px dashed #aaaaaa";
  const marginLeft = 0; //listVariant === "TRIP" ? 0 : 0;
  const marginRight = 0; // listVariant === "TRIP" ? 0 : 100;
  const pageSize = listVariant === "TRIP" ? 5 : 3;
  const rowsPerPageOptions = listVariant === "TRIP" ? [5, 10, 15] :[3, 6, 9];
  
  return (isAuthenticated &&
      <div style={{ marginLeft: marginLeft, marginRight: marginRight}} >
        <MaterialTable
            tableRef={tableRef}
            style={{
              backgroundColor: "#fdfdfd",
              border: border,
              boxShadow: "none",
              borderRadius: 0,
            }}
            options={{
              actionsColumnIndex: -1,
              cellStyle: {
                padding: "8px 2px",
              },
              headerStyle: {
                backgroundColor: theme.palette.childTableHeader.backgroundColor,
                color: theme.palette.childTableHeader.textColor,
                padding: "2px",
              },
              toolbarButtonAlignment: "right",
              pageSize: pageSize,
              detailPanelType: "single",
              padding: "dense",
            }}
            components={{
              ...defaultComponents,
              Pagination: (props) => (
                  <TablePagination
                      {...props}
                      rowsPerPageOptions={rowsPerPageOptions}
                  />
              ),
            }}
            title={listVariant === "TRIP" ? "Trip Ad-hoc Messaging" : "Trip Sector Ad-hoc Messaging"}
            columns={getColumns({canEdit: state.canEdit})}
            data={tripMessaging.data}
            editable={{
              isEditable: () => {
                return state.canEdit;
              },
              isDeletable: () => {
                return state.canEdit;
              },
              onRowAdd: state.canEdit
                  ? (newData) => new Promise((resolve, reject) => {
                    handleAddTripMessaging.mutate({record: {...newData, tripUuid: tripUuid, tripLegUuid: tripLegUuid}, resolve, reject, extraKeys, listVariant});
                  })
                  : undefined,
              onRowUpdate: (newData, oldData) =>
                  new Promise((resolve, reject) => {
                    handleUpdateTripMessaging.mutate({record: {...newData, tripUuid: tripUuid, tripLegUuid: oldData.tripLegUuid}, oldRecord: oldData, resolve, reject, extraKeys, listVariant});
                  }),
              onRowDelete: (oldData) =>
                  new Promise((resolve, reject) => {
                    handleDeleteTripMessaging.mutate({oldRecord: oldData, resolve, reject, extraKeys, listVariant});
                  }),
            }}
        />
      </div>
  );

}

export default TripMessagingList;
